/* eslint-disable no-shadow */
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import PlayerCardV3 from '../../../../../components/cards/PlayerCardV3';
import Rating from '../../../../../components/cards/PlayerCard/SharedComponents/Rating';
import { sportTypeIds, utrTypeIds } from '../../../../../utils/eventConstants';
import { getPlayerPaymentStatus, getPlayerRegStatusV3, getPlayerPosition, getPlayerRegDateV3, getTeamRegDateV3 } from '../../../../../utils';
import { gameTypes } from '../../../../../utils/constants';
import AvatarV2 from '../../../../../components/cards/PlayerCard/ListViewComponents/AvatarV2';
import PlayerDetails from '../../../../../components/cards/PlayerCard/ListViewComponents/PlayerDetails';
import { getPlayerCardPlayerDetails } from '../../../../../utils/formatting';
import { authShape, menuOptionShape, playerShape, timeZoneShape, divisionShape } from '../../../../../propTypes';

const WaitlistPlayerCard = props => {
    const {
        gridLayout,
        hideRatings,
        hideUtrLabel,
        isCurrentMemberAdmin,
        player,
        ptsRank,
        showDecimals,
        showHighSchoolName,
        showPickleBallRatings = true,
        showRatingNotLoggedIn,
        showRegStatus,
        showTennisRatings = true,
        sportTypeId,
        utrTypeId,
        timeZone,
        isFirstPlayer,
        isDoubles,
        playerDivision,
    } = props;

    const { showSaveBookmark, 'data-testid': dataTestId, ...playerCardProps } = props;

    const auth = useSelector(({ auth }) => auth);
    const user = auth?.user;
    const { firstName, lastName, nationality, playerProfileImages, ratingStatusSingles, ratingStatusDoubles, playerId } = player;

    const descriptionItems = getPlayerCardPlayerDetails(player, isCurrentMemberAdmin, showHighSchoolName);

    const regStatus = getPlayerRegStatusV3(playerDivision);
    const regDate = getPlayerRegDateV3(playerDivision, timeZone?.value, true);
    const teamRegDate = isFirstPlayer && getTeamRegDateV3(playerDivision, timeZone?.value);
    const paymentStatus = getPlayerPaymentStatus(playerDivision?.paymentStatusTypeId);
    const position = getPlayerPosition(playerDivision);

    return (
        <PlayerCardV3 {...playerCardProps} showSaveBookmark={showSaveBookmark} data-testid={dataTestId}>
            <div className={`col-12`}>
                <div className="d-grid w-100" style={{ gridTemplateColumns: gridLayout.join(' ') }}>
                    <div className={`d-flex aic oh`}>
                        <div className={`values ellipsis`} title={position ? position.bucket : ''}>
                            {position ? position.bucket : ''}
                        </div>
                    </div>
                    <div className={`d-flex aic oh pr8`}>
                        <AvatarV2 playerImages={playerProfileImages} nationality={nationality} player={player} />
                        <PlayerDetails
                            firstName={firstName}
                            lastName={lastName}
                            description={descriptionItems}
                            playerId={playerId}
                            isCurrentMemberAdmin={isCurrentMemberAdmin}
                        />
                    </div>
                    {(!hideRatings && (
                        <>
                            {showTennisRatings && (
                                <>
                                    <div className={`d-flex aic pr8`}>
                                        {(user || showRatingNotLoggedIn) && (
                                            <Rating
                                                utrType={utrTypeId}
                                                ratingType={gameTypes.SINGLES}
                                                ratingStatus={ratingStatusSingles}
                                                player={player}
                                                showDecimals={showDecimals}
                                                hideUtrLabel={hideUtrLabel}
                                                sportTypeId={sportTypeId}
                                            />
                                        )}
                                    </div>
                                    <div className={`d-flex aic pr8`}>
                                        {(user || showRatingNotLoggedIn) && (
                                            <Rating
                                                utrType={utrTypeId}
                                                ratingType={gameTypes.DOUBLES}
                                                ratingStatus={ratingStatusDoubles}
                                                player={player}
                                                showDecimals={showDecimals}
                                                hideUtrLabel={hideUtrLabel}
                                                sportTypeId={sportTypeId}
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                            {showPickleBallRatings && (
                                <>
                                    <div className={`d-flex aic pr8`}>
                                        {(user || showRatingNotLoggedIn) && (
                                            <Rating
                                                utrType={utrTypeId}
                                                ratingType={gameTypes.SINGLES}
                                                player={player}
                                                showDecimals={showDecimals}
                                                hideUtrLabel={hideUtrLabel}
                                                sportTypeId={sportTypeId}
                                                isPickleballRating
                                            />
                                        )}
                                    </div>
                                    <div className={`d-flex aic pr8`}>
                                        <Rating
                                            utrType={utrTypeId}
                                            ratingType={gameTypes.DOUBLES}
                                            player={player}
                                            showDecimals={showDecimals}
                                            hideUtrLabel={hideUtrLabel}
                                            sportTypeId={sportTypeId}
                                            isPickleballRating
                                        />
                                    </div>
                                </>
                            )}
                            {!!ptsRank && (
                                <div className={`d-flex aic pr8`}>
                                    <div className="values">
                                        <div
                                            className={'value'}
                                            title={utrTypeId === utrTypeIds.SANCTIONED ? ratingStatusDoubles : ''}
                                        >
                                            {ptsRank}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ))}
                    {isCurrentMemberAdmin && (
                        <div className={`d-flex aic oh pr8`}>
                            <div className={`values ellipsis`} title={regDate}>
                                {regDate}
                            </div>
                        </div>
                    )}
                    {isCurrentMemberAdmin && isDoubles && (
                        <div className={`d-flex aic oh pr8`}>
                            <div className={`values ellipsis`} title={teamRegDate}>
                                {teamRegDate}
                            </div>
                        </div>
                    )}
                    {showRegStatus && (
                        <div className={`d-flex aic oh pr8`}>
                            <div className={`values ${regStatus.color} ellipsis`} title={regStatus.label}>
                                {regStatus.label}
                            </div>
                        </div>
                    )}
                    {isCurrentMemberAdmin && (
                        <div className={`d-flex aic oh pr8`}>
                            <div className={`values ${paymentStatus.color} ellipsis`} title={paymentStatus.label}>
                                {paymentStatus.label}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </PlayerCardV3>
    );
};

WaitlistPlayerCard.propTypes = {
    addOptionsSpace: PropTypes.bool,
    auth: authShape.isRequired,
    gridLayout: PropTypes.arrayOf(PropTypes.string).isRequired,
    hideRatings: PropTypes.bool,
    hideUtrLabel: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    isSelected: PropTypes.bool,
    menuOptions: PropTypes.arrayOf(menuOptionShape),
    onMenuSelected: PropTypes.func,
    onSelectClick: PropTypes.func,
    player: playerShape.isRequired,
    ptsRank: PropTypes.number,
    sendMessage: PropTypes.func,
    showCheckbox: PropTypes.bool,
    showDecimals: PropTypes.bool,
    showHighSchoolName: PropTypes.bool,
    showPickleBallRatings: PropTypes.bool,
    showPlayerPaymentStatus: PropTypes.bool,
    showRatingNotLoggedIn: PropTypes.bool,
    showRegStatus: PropTypes.bool,
    showTennisRatings: PropTypes.bool,
    sportTypeId: PropTypes.number,
    utrTypeId: PropTypes.number,
    timeZone: timeZoneShape,
    isFirstPlayer: PropTypes.bool,
    isDoubles: PropTypes.bool,
    playerDivision: divisionShape.isRequired,
    showSaveBookmark: PropTypes.bool,
    'data-testid': PropTypes.string,
};

WaitlistPlayerCard.defaultProps = {
    addOptionsSpace: true,
    hideRatings: false,
    hideUtrLabel: false,
    isCurrentMemberAdmin: false,
    isSelected: false,
    menuOptions: null,
    onMenuSelected: () => { },
    onSelectClick: () => { },
    ptsRank: 0,
    sendMessage: () => { },
    showDecimals: false,
    showCheckbox: false,
    showHighSchoolName: false,
    showPickleBallRatings: false,
    showPlayerPaymentStatus: false,
    showRatingNotLoggedIn: false,
    showRegStatus: false,
    showTennisRatings: false,
    sportTypeId: sportTypeIds.TENNIS,
    utrTypeId: utrTypeIds.MYUTR,
    timeZone: null,
    isFirstPlayer: false,
    isDoubles: false,
    showSaveBookmark: false,
    'data-testid': null,
};

export default WaitlistPlayerCard;
